import { batch, Owner } from "solid-js";
import { reconcile } from "solid-js/store";

import PaymentMethod, {
  PaymentMethodCanada,
  PaymentMethodUsa,
} from "@repo/kikoff-models/payments/PaymentMethod";
import t from "@repo/localization/string.macro";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import usRpc from "@repo/protobuf/api/usRpc";
import Stripe from "@repo/ui/external/Stripe";
import UBoolean from "@repo/utils/UBoolean";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { track } from "~/analytics/client";

const initialState = {
  byId: {} as Record<PaymentMethod.Id, PaymentMethod>,
  all: undefined as undefined | string[],
  stripe: { clientSecret: null as null | string },
};

export const { Provider, usePaymentMethods, Context } = FeatureStore.init(
  "PaymentMethods",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const accessors = {
      loaded: attachAction(
        () => !!store.all,
        () => actions.fetch()
      ),
      all: attachAction(
        () => store.all?.map((id) => store.byId[id]!) || [],
        () => actions.fetch(),
        () => () => store.all
      ),
      stripe: {
        clientSecret: attachAction(
          () => store.stripe.clientSecret,
          () => actions.stripe.fetchClientSecret()
        ),
      },
      byType: attachAction(
        (type: PaymentMethod.Type): PaymentMethod[] =>
          accessors.all().filter((pm) => pm.type === type),
        () => actions.fetch()
      ),
      byId: attachAction(
        (id: PaymentMethod.Id | undefined) => id && store.byId[id],
        () => actions.fetch(),
        () => () => store.all
      ),
    };

    const mutations = {
      // TODO: Remove undefined options after we support all payment method types
      one(paymentMethod: PaymentMethod | undefined) {
        if (!paymentMethod) return;
        setStore("byId", paymentMethod.id, reconcile(paymentMethod));
      },
      add(paymentMethod: PaymentMethod | undefined) {
        if (!paymentMethod)
          throw new Error(
            "Payment method will be undefined if normalization doesn't support type, should not be able to add unsupported payment method."
          );
        batch(() => {
          mutations.one(paymentMethod);
          setStore("all", (all) => all && [...all, paymentMethod.id]);
        });
      },
      many(paymentMethods: (PaymentMethod | undefined)[]) {
        batch(() => {
          setStore(
            "all",
            paymentMethods.map((pm) => pm?.id).filter(UBoolean.isTruthy)
          );

          for (const pm of paymentMethods) {
            mutations.one(pm);
          }
        });
      },
      all(paymentMethods: (PaymentMethod | undefined)[]) {
        batch(() => {
          mutations.many(paymentMethods);

          setStore(
            "all",
            paymentMethods.map((pm) => pm?.id).filter(UBoolean.isTruthy)
          );
        });
      },

      remove(paymentMethodId: PaymentMethod.Id) {
        batch(() => {
          setStore("all", (all) => all?.filter((id) => id !== paymentMethodId));
          // TODO: Make this lint rule more flexible
          // eslint-disable-next-line no-unused-vars
          setStore("byId", ({ [paymentMethodId]: _, ...byId }) => byId);
        });
      },
    };

    const us = {
      actions: {
        fetch() {
          return usRpc.PaymentMethods.getPaymentMethods({})
            .SUCCESS((data) => {
              mutations.all(
                data.paymentMethods.map(PaymentMethodUsa.normalize)
              );
            })
            .elseThrow(t`Failed to fetch payment methods.`);
        },
        stripe: {
          fetchClientSecret: () =>
            usRpc.PaymentMethods.addPaymentMethod({})
              .SUCCESS((data) => {
                setStore(
                  "stripe",
                  "clientSecret",
                  data.setupIntent?.clientSecret!
                );
              })
              .elseThrow(t`Failed to connect to Stripe.`),
          async addPaymentMethod(owner: Owner) {
            const { setupIntent, error } =
              await Stripe.PaymentElement.confirmSetup(owner);

            if (error) throw new Error(error.message);

            return usRpc.PaymentMethods.triggerPostAddPaymentMethodHook({
              setupIntentId: setupIntent.id,
            })
              .SUCCESS(async ({ paymentMethodToken }) => {
                await actions.fetch();
                return paymentMethodToken as PaymentMethod.Id;
              })
              .elseThrow(t`Failed to finalize payment method.`);
          },
        },
        remove(paymentMethodId: PaymentMethod.Id) {
          return usRpc.PaymentMethods.deactivatePaymentMethod({
            token: paymentMethodId,
          })
            .SUCCESS(() => {
              actions.fetch();
            })
            .elseThrow(t`Failed to remove payment method.`);
        },
      },
    };

    const canada = {
      actions: {
        fetch() {
          return canadaRpc.PaymentMethods.listPaymentMethods({}).then(
            (data) => {
              mutations.all(
                data.paymentMethods.map(PaymentMethodCanada.normalize)
              );
            }
          );
        },
        stripe: {
          fetchClientSecret: () =>
            canadaRpc.PaymentMethods.initiateAddPaymentMethod({}).then(
              (data) => {
                setStore("stripe", "clientSecret", data.clientSecret);
              }
            ),
          async addPaymentMethod(owner: Owner) {
            track.paymentMethod.add("attempt");

            try {
              const { setupIntent, error } =
                await Stripe.PaymentElement.confirmSetup(owner);

              if (error) throw new Error(error.message);

              // Must await here to catch
              return await canadaRpc.PaymentMethods.completeAddPaymentMethod({
                setupIntentId: setupIntent.id,
              }).then(async ({ paymentMethod }) => {
                track.paymentMethod.add("success");

                mutations.add(PaymentMethodCanada.normalize(paymentMethod!));
                setStore("stripe", "clientSecret", null);

                return paymentMethod!.id as PaymentMethod.Id;
              });
            } catch (e) {
              track.paymentMethod.add("failure", {
                errors: e instanceof Error ? [e.message] : [],
              });
              throw e;
            }
          },
        },
        remove(paymentMethodId: PaymentMethod.Id) {
          return canadaRpc.PaymentMethods.deactivatePaymentMethod({
            paymentMethodId,
          }).then(() => {
            mutations.remove(paymentMethodId);
          });
        },
      },
    };

    const actions = wrapActions(
      import.meta.env.API_ADAPTER === "canada" ? canada.actions : us.actions
    );

    return { accessors, actions };
  }
);
